import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { updateFilterPartial } from '../../actions/eventFiltersActions';
import { APP_ROUTES } from '../../constants/routes';
import { basic } from '../../theme/colors';
import { Event } from '../../interfaces';
import { Geocode } from '../event_details';
import { initialEventFiltersState } from '../../reducers/eventFilters/eventFiltersState';
import { getBlockName } from '../../utils';
import { useEventPreview } from '../../hooks';
import { Permission } from './adminOnly';
import { EventTypeIdentifier } from '../../enums';

export const Address = styled.span<{ variant: 'primary' | 'secondary' }>`
  font-size: 12px;
  color: ${({ variant }) => (variant === 'primary' ? basic.haydenAccent : basic.haydenBrand)};
  margin-top: 1em;
  margin-bottom: 1em;
  display: block;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const StyledDropdownMenu = styled(Dropdown.Menu)`
  padding: 0.5rem 0;
`;

const StyledDropdownItem = styled(Dropdown.Item)`
  font-size: 14px;
  padding: 0.5rem 1rem;

  &:hover {
    background-color: ${basic.haydenHover};
  }
`;

interface Props {
  event: Event;
}

interface CustomToggleProps {
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

// Custom toggle component that forwards refs and handles click events
const CustomToggle = React.forwardRef<HTMLSpanElement, CustomToggleProps>((props, ref) => {
  const { onClick, children } = props;

  return (
    <span
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label="Open address actions menu"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          // Create a synthetic mouse event for keyboard activation
          const mouseEvent = new MouseEvent('click', { bubbles: true });
          onClick(mouseEvent as unknown as React.MouseEvent);
        }
      }}
    >
      {children}
    </span>
  );
});

export const AddressActionable = (props: Props) => {
  const { event } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const { closeEventModal } = useEventPreview();

  const blockName = getBlockName(event, true);
  let busStopId = '';

  const blockId = event?.metadata?.route_metadata?.block_id;

  if (
    blockId &&
    (event.event_type_identifier === EventTypeIdentifier.BUS_STOP ||
      event.event_type_identifier === EventTypeIdentifier.BUS_STOP_MATCH ||
      event.event_type_identifier === EventTypeIdentifier.BUS_STOP_PARKING ||
      event.event_type_identifier === EventTypeIdentifier.BUS_STOP_TEST)
  ) {
    busStopId = blockId;
  }

  const copyBlockName = () => {
    navigator.clipboard
      .writeText(blockName)
      .then(() => {
        toast.info('Copied!');
      })
      .catch(() => {
        toast.error('Failed to copy to clipboard');
      });
  };

  const filterToThisBlockName = () => {
    dispatch(updateFilterPartial({ block_names: [blockName] }));
    closeEventModal();
    history.push(APP_ROUTES.portalList);
  };

  const viewAllEventsForThisBlockName = () => {
    dispatch(
      updateFilterPartial({
        ...initialEventFiltersState.filter,
        block_names: [blockName],
        selectedFilters: [],
      }),
    );
    closeEventModal();
    setTimeout(() => {
      history.push(APP_ROUTES.portalList);
    }, 250);
  };

  const filterToThisBusStop = () => {
    if (!busStopId) return;
    dispatch(updateFilterPartial({ bus_stop_ids: [busStopId] }));
    closeEventModal();
    history.push(APP_ROUTES.portalList);
  };

  const viewAllEventsForThisBusStop = () => {
    if (!busStopId) return;
    dispatch(
      updateFilterPartial({
        ...initialEventFiltersState.filter,
        bus_stop_ids: [busStopId],
        selectedFilters: [],
      }),
    );
    closeEventModal();
    setTimeout(() => {
      history.push(APP_ROUTES.portalList);
    }, 250);
  };

  const copyBusStopId = () => {
    if (!busStopId) return;
    navigator.clipboard
      .writeText(busStopId)
      .then(() => {
        toast.info('Copied!');
      })
      .catch(() => {
        toast.error('Failed to copy bus stop ID.');
      });
  };

  return (
    // @ts-expect-error react-bootstrap type definitions issue
    <Dropdown drop="up">
      <Dropdown.Toggle as={CustomToggle} id="address-dropdown">
        <Address variant="primary">
          <Geocode event={event} />
        </Address>
      </Dropdown.Toggle>

      <StyledDropdownMenu>
        <StyledDropdownItem onClick={copyBlockName}>Copy block name</StyledDropdownItem>

        <Permission permission="events:read:list">
          <Dropdown.Divider />
          <StyledDropdownItem onClick={filterToThisBlockName}>Filter results to this block name</StyledDropdownItem>
          <Dropdown.Divider />
          <StyledDropdownItem onClick={viewAllEventsForThisBlockName}>
            View all events for this block name
          </StyledDropdownItem>
        </Permission>

        {busStopId && (
          <Permission permission="events:read:list">
            <Dropdown.Divider />
            <StyledDropdownItem onClick={copyBusStopId}>Copy bus stop ID</StyledDropdownItem>

            <Dropdown.Divider />

            <StyledDropdownItem onClick={filterToThisBusStop}>
              Filter results to this bus stop ({busStopId})
            </StyledDropdownItem>
            <Dropdown.Divider />
            <StyledDropdownItem onClick={viewAllEventsForThisBusStop}>
              View all events for this bus stop ({busStopId})
            </StyledDropdownItem>
          </Permission>
        )}
      </StyledDropdownMenu>
    </Dropdown>
  );
};
